import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../../data";
import type { Account } from "../../app/session";
import { StartRahmen } from "../../components/shell/StartRahmen";
import { Loading } from "../../components/States";
import { fehlerText } from "../../lib/fehler";
import { t } from "../../lib/sprache";
import { openBillingPortal } from "../agentur/einstellungen/data";

/**
 * Eigene Seite „Zahlungsmethode ändern“ (/zahlungsmethode, Inhaber 13.10.2026). Die Zahlungsmails von Stripe (Zahlung
 * gescheitert, Karte läuft ab) verlinken hierher statt auf eine Seite von Stripe: Die Kundin meldet sich an, sieht ihr Konto
 * und kommt mit einem Klick in das gesicherte Formular von Stripe, direkt beim Ändern der Zahlungsart. Kartendaten nimmt
 * weiterhin nur Stripe entgegen.
 */
interface AboZeile { org_id: string; stripe_customer_id: string | null; status: string | null; payment_provider: string | null }

export function ZahlungsmethodeAendern({ account, onSignOut }: { account: Account; onSignOut: () => void }) {
  const admin = account.memberships.filter((m) => m.role === "chef_manager" && !!account.orgs[m.org_id]);
  const ids = admin.map((m) => m.org_id);
  const abos = useQuery({
    queryKey: ["zahlungsmethode-abos", ids.join(",")], enabled: ids.length > 0,
    queryFn: () => api.select<AboZeile>("subscriptions", { columns: "org_id, stripe_customer_id, status, payment_provider", filters: [["org_id", "in", ids]] }),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const oeffnen = async (orgId: string) => {
    setBusy(orgId);
    setFehler(null);
    try {
      window.location.assign(await openBillingPortal(api, orgId, "payment_method_update"));
    } catch (e) {
      setFehler(fehlerText(e));
      setBusy(null);
    }
  };
  // Praxistest P-5 b: Ein Abo über PayPal hat seine Zahlungsart bei PayPal; hier nicht zum Stripe-Formular schicken
  const perPaypal = admin.filter((m) => (abos.data ?? []).some((a) => a.org_id === m.org_id && a.payment_provider === "paypal"));
  const mitStripe = admin.filter((m) => !perPaypal.includes(m) && (abos.data ?? []).some((a) => a.org_id === m.org_id && !!a.stripe_customer_id));
  return (
    <StartRahmen title={t("Zahlungsmethode ändern", "Change payment method")} onSignOut={onSignOut}>
      <div className="panel stack tk-zahlungsmethode">
        <h1>{t("Zahlungsmethode ändern", "Change payment method")}</h1>
        {ids.length > 0 && abos.isLoading ? <Loading /> : admin.length === 0 ? (
          <>
            <p>{t("Die Zahlungsart verwaltet der Admin deines Kontos. Bitte gib ihm Bescheid; er kommt über diese Seite direkt zum Formular.", "The payment method is managed by the admin of your account. Please let them know; this page takes them straight to the form.")}</p>
            <div className="row"><Link className="btn" to="/">{t("Zum Portal", "Go to the portal")}</Link></div>
          </>
        ) : mitStripe.length === 0 && perPaypal.length > 0 ? (
          <>
            <p>{t("Dein Abo läuft über PayPal. Die Zahlungsquelle (Konto oder Karte) änderst du direkt in deinem PayPal-Konto unter Einstellungen · Zahlungen · Automatische Zahlungen · CreatorOffice.", "Your subscription runs via PayPal. You change the funding source (account or card) directly in your PayPal account under Settings · Payments · Automatic payments · CreatorOffice.")}</p>
            <div className="row"><Link className="btn" to="/agentur/einstellungen/abo">{t("Zu Abo & Tarif", "Open subscription & plan")}</Link></div>
          </>
        ) : mitStripe.length === 0 ? (
          <>
            <p>{t("Für dein Konto ist bei uns keine Karte und keine Lastschrift hinterlegt. Unter Abo & Tarif siehst du, wie dein Konto abgerechnet wird, und kannst eine Zahlungsart hinterlegen.", "Your account has no card and no direct debit on file with us. Under Subscription & plan you can see how your account is billed and add a payment method.")}</p>
            <div className="row"><Link className="btn primary" to="/agentur/einstellungen/abo">{t("Zu Abo & Tarif", "Open subscription & plan")}</Link></div>
          </>
        ) : (
          <>
            <p>{t("Hier hinterlegst du eine neue Karte oder ein neues Konto für dein Abo. Die Eingabe läuft im gesicherten Formular unseres Zahlungsdienstes Stripe; danach kommst du zurück ins Portal. Dein Tarif und deine Daten bleiben unverändert.",
              "Here you add a new card or bank account for your subscription. You enter it in the secure form of our payment provider Stripe; afterwards you return to the portal. Your plan and your data stay unchanged.")}</p>
            {mitStripe.map((m) => (
              <div key={m.org_id} className="row between tk-zahlungsmethode-konto">
                <strong>{account.orgs[m.org_id]!.name}</strong>
                <button type="button" className="primary" disabled={!!busy} aria-busy={busy === m.org_id} onClick={() => void oeffnen(m.org_id)}>
                  {busy === m.org_id ? t("Formular wird geöffnet …", "Opening form …") : t("Zahlungsmethode jetzt ändern", "Change payment method now")}
                </button>
              </div>
            ))}
            {fehler && <p className="tk-error" role="alert">{fehler}</p>}
            <small>{t("Rechnungen und den Stand deines Abos findest du unter ", "You find invoices and the state of your subscription under ")}<Link to="/agentur/einstellungen/abo">{t("Abo & Tarif", "Subscription & plan")}</Link>.</small>
          </>
        )}
        <small>{t("Fragen oder klappt etwas nicht?", "Questions, or something not working?")} <a href="mailto:support@creatoroffice.de">support@creatoroffice.de</a></small>
      </div>
    </StartRahmen>
  );
}
